import { c as _c } from "react/compiler-runtime";
import { IconArchive, IconBell, IconBellCog, IconChecks, IconInbox } from "@tabler/icons-react";
import Link from "next/link";
import { useEffect, useRef, useState, useTransition } from "react";
import { NotificationList } from "@/components/notifications/notification-list";
import { useNotificationsData } from "@/hooks/use-notifications-data";
import { useRouter } from "@/hooks/use-router";
import type { NotificationData } from "@domainstack/types";
import { Button } from "@domainstack/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@domainstack/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@domainstack/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
import { cn } from "@domainstack/ui/utils";
export function NotificationsPopover() {
  const $ = _c(85);
  const router = useRouter();
  const loadMoreRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const [view, setView] = useState("inbox");
  const [open, setOpen] = useState(false);
  const [, startTransition] = useTransition();
  const autoMarkedThisOpenRef = useRef(false);
  const skipAutoMarkOnCloseRef = useRef(false);
  const filter = view === "inbox" ? "unread" : "read";
  let t0;
  if ($[0] !== filter || $[1] !== open) {
    t0 = {
      filter,
      enabled: open
    };
    $[0] = filter;
    $[1] = open;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    notifications,
    count,
    showLoading,
    hasNextPage,
    isFetchingNextPage,
    isError: isNotificationsError,
    markAllRead,
    markRead,
    fetchNextPage,
    getLatestUnreadCount
  } = useNotificationsData(t0);
  let t1;
  let t2;
  if ($[3] !== open) {
    t1 = () => {
      if (open) {
        autoMarkedThisOpenRef.current = false;
        skipAutoMarkOnCloseRef.current = false;
      }
    };
    t2 = [open];
    $[3] = open;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] !== getLatestUnreadCount || $[7] !== markAllRead || $[8] !== view) {
    t3 = () => {
      if (autoMarkedThisOpenRef.current) {
        return;
      }
      if (markAllRead.isPending) {
        return;
      }
      if (view !== "inbox") {
        return;
      }
      const latestUnreadCount = getLatestUnreadCount();
      if (latestUnreadCount <= 0) {
        return;
      }
      autoMarkedThisOpenRef.current = true;
      markAllRead.mutate(undefined, {
        onError: () => {
          autoMarkedThisOpenRef.current = false;
        }
      });
    };
    $[6] = getLatestUnreadCount;
    $[7] = markAllRead;
    $[8] = view;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const maybeAutoMarkAllRead = t3;
  let t4;
  if ($[10] !== maybeAutoMarkAllRead) {
    t4 = () => {
      maybeAutoMarkAllRead();
      setOpen(false);
    };
    $[10] = maybeAutoMarkAllRead;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const closePopover = t4;
  let t5;
  if ($[12] !== markRead) {
    t5 = notification => {
      if (!notification.readAt) {
        markRead.mutate({
          id: notification.id
        });
      }
      skipAutoMarkOnCloseRef.current = true;
      setOpen(false);
    };
    $[12] = markRead;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleNotificationClick = t5;
  let t6;
  let t7;
  if ($[14] !== fetchNextPage || $[15] !== hasNextPage || $[16] !== isFetchingNextPage || $[17] !== open) {
    t6 = () => {
      const scrollContainer = scrollAreaRef.current;
      const loadMoreElement = loadMoreRef.current;
      if (!open || !loadMoreElement || !hasNextPage || isFetchingNextPage) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      }, {
        threshold: 0.1,
        root: scrollContainer
      });
      observer.observe(loadMoreElement);
      return () => observer.disconnect();
    };
    t7 = [fetchNextPage, hasNextPage, isFetchingNextPage, open];
    $[14] = fetchNextPage;
    $[15] = hasNextPage;
    $[16] = isFetchingNextPage;
    $[17] = open;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  useEffect(t6, t7);
  let t8;
  if ($[20] !== maybeAutoMarkAllRead) {
    t8 = nextOpen => {
      if (!nextOpen) {
        if (skipAutoMarkOnCloseRef.current) {
          skipAutoMarkOnCloseRef.current = false;
        } else {
          maybeAutoMarkAllRead();
        }
      }
      setOpen(nextOpen);
    };
    $[20] = maybeAutoMarkAllRead;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = `Notifications${count > 0 ? ` (${count})` : ""}`;
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <IconBell />;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== count) {
    t11 = count > 0 && <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-destructive ring-2 ring-background" aria-hidden={true} />;
    $[23] = count;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t11 || $[26] !== t9) {
    t12 = <TooltipTrigger render={<PopoverTrigger render={<Button variant="ghost" size="icon-sm" className="relative" aria-label={t9}>{t10}{t11}</Button>} />} />;
    $[25] = t11;
    $[26] = t9;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  const t13 = count > 0 ? `Notifications (${count})` : "Notifications";
  let t14;
  if ($[28] !== t13) {
    t14 = <TooltipContent>{t13}</TooltipContent>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t14) {
    t15 = <Tooltip>{t12}{t14}</Tooltip>;
    $[30] = t12;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      side: "none",
      align: "shift"
    };
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <h4 className="text-[15px] font-semibold">Notifications</h4>;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== closePopover || $[36] !== router) {
    t18 = e => {
      e.preventDefault();
      router.push("/settings/notifications");
      closePopover();
    };
    $[35] = closePopover;
    $[36] = router;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Link href="/settings/notifications"><IconBellCog className="size-3.5 shrink-0 text-foreground/90" /><span className="sr-only">Settings</span></Link>;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t18) {
    t20 = <TooltipTrigger render={<Button variant="ghost" size="icon-sm" aria-label="Notification settings" nativeButton={false} onClick={t18} render={t19} />} />;
    $[39] = t18;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <TooltipContent>Settings</TooltipContent>;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== t20) {
    t22 = <div className="flex items-center justify-between pt-2 pr-4 pb-1 pl-4">{t17}<Tooltip>{t20}{t21}</Tooltip></div>;
    $[42] = t20;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== maybeAutoMarkAllRead || $[45] !== startTransition || $[46] !== view) {
    t23 = v => {
      const nextView = v as typeof view;
      if (view === "inbox" && nextView === "archive") {
        maybeAutoMarkAllRead();
      }
      startTransition(() => setView(nextView));
      if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollTop = 0;
      }
    };
    $[44] = maybeAutoMarkAllRead;
    $[45] = startTransition;
    $[46] = view;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <IconInbox className="!text-inherit" aria-hidden={true} />;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== count) {
    t25 = count > 0 && <span className="ml-0.5 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-foreground px-1.5 text-[10px] font-medium text-background tabular-nums">{count > 99 ? "99+" : count}</span>;
    $[49] = count;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t25) {
    t26 = <TabsTrigger value="inbox" className="flex-initial gap-2 text-[13px] transition-colors hover:text-foreground">{t24}Inbox{t25}</TabsTrigger>;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <TabsTrigger value="archive" className="flex-initial gap-2 text-[13px] transition-colors hover:text-foreground"><IconArchive className="!text-inherit" aria-hidden={true} />Archive</TabsTrigger>;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== count || $[55] !== markAllRead || $[56] !== view) {
    t28 = view === "inbox" && count > 0 && <div className="ml-auto flex items-center"><button type="button" onClick={() => markAllRead.mutate()} disabled={markAllRead.isPending} className={cn("flex h-full items-center gap-1.5 px-2 text-[13px] font-medium text-muted-foreground transition-colors", "hover:text-foreground focus-visible:text-foreground", "focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-1 focus-visible:outline-none", "disabled:pointer-events-none disabled:opacity-50")} aria-label="Clear all notifications"><IconChecks className="size-4" aria-hidden={true} /><span className="max-sm:sr-only">Clear all</span></button></div>;
    $[54] = count;
    $[55] = markAllRead;
    $[56] = view;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== t26 || $[59] !== t28) {
    t29 = <TabsList variant="line">{t26}{t27}{t28}</TabsList>;
    $[58] = t26;
    $[59] = t28;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== t23 || $[62] !== t29 || $[63] !== view) {
    t30 = <div className="pl-1.5"><Tabs value={view} onValueChange={t23}>{t29}</Tabs></div>;
    $[61] = t23;
    $[62] = t29;
    $[63] = view;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t22 || $[66] !== t30) {
    t31 = <div className="shrink-0 border-b border-border bg-card/60">{t22}{t30}</div>;
    $[65] = t22;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== closePopover || $[69] !== handleNotificationClick || $[70] !== hasNextPage || $[71] !== isFetchingNextPage || $[72] !== isNotificationsError || $[73] !== notifications || $[74] !== showLoading || $[75] !== view) {
    t32 = <NotificationList notifications={notifications} isLoading={showLoading} isError={isNotificationsError} view={view} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} loadMoreRef={loadMoreRef} scrollAreaRef={scrollAreaRef} onNotificationClick={handleNotificationClick} onClosePopover={closePopover} />;
    $[68] = closePopover;
    $[69] = handleNotificationClick;
    $[70] = hasNextPage;
    $[71] = isFetchingNextPage;
    $[72] = isNotificationsError;
    $[73] = notifications;
    $[74] = showLoading;
    $[75] = view;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== t31 || $[78] !== t32) {
    t33 = <PopoverContent className="overflow-hidden bg-background p-0 max-sm:!right-0 max-sm:!left-0 max-sm:!mx-auto max-sm:w-[calc(100vw-1rem)] max-sm:!translate-x-0 sm:w-[400px]" align="end" side="bottom" collisionAvoidance={t16} collisionPadding={8}><div className="flex max-h-[calc(min(100dvh-6rem,560px))] flex-col">{t31}{t32}</div></PopoverContent>;
    $[77] = t31;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== open || $[81] !== t15 || $[82] !== t33 || $[83] !== t8) {
    t34 = <Popover open={open} onOpenChange={t8}>{t15}{t33}</Popover>;
    $[80] = open;
    $[81] = t15;
    $[82] = t33;
    $[83] = t8;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  return t34;
}